import { h, FunctionComponent } from 'preact';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Page } from '~src/domains/common/enums/api';
import {
  AlertNames,
  Brand,
  QuebecOption,
} from '~src/domains/common/enums/common';
import {
  Country,
  RegionsCA,
  RegionsUS,
} from '~src/domains/common/enums/localization';
import { general } from '~src/domains/common/state/commonReducer';
import {
  brandSelector,
  customerInfoSelector,
  isOnboardSpendSelector,
  localeSelector,
  mcodeSelector,
  optionsSelector,
  pageSelector,
} from '~src/domains/common/state/commonSelectors';
import { StartOption } from '~src/domains/start/enums/start';
import * as startFormActions from '~src/domains/start/state/startFormReducer';
import { setMarketingCommunicationOptIn } from '~src/domains/start/state/startReducer';
import {
  marketingCommunicationOptInDirtySelector,
  marketingCommunicationOptInSelector,
  startFormSelector,
} from '~src/domains/start/state/startSelectors';
import * as styles from '~src/styles/domains/start/StartPage.module.scss';
import { updateFormMeta } from '~src/utils/forms';
import { formatCustomerNames } from '~src/utils/helpers';
import {
  trackClick,
  trackFocus,
  trackFormBlur,
  trackPageMount,
} from '~src/utils/tracking';
import { validate, validationFunctions } from '~src/utils/validation';
import { xhrNext } from '~src/utils/xhr';

import Alerter from '~src/domains/common/components/Alerter';
import SimpleButton from '~src/domains/common/components/Button';
import CheckboxGroup from '~src/domains/common/components/CheckboxGroup';
import StartFootnotes from '~src/domains/start/components/StartFootnotes';
import StartInfo from '~src/domains/start/components/StartInfo';
import StartInputs from '~src/domains/start/components/StartInputs';
import StartPleaseEnsure from '~src/domains/start/components/StartPleaseEnsure';
import { FlexpayLogo } from '../common/assets/svg';
import StartOnboardInfo from './components/StartOnboardInfo';

const StartPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isOnBoardSpend = useSelector(isOnboardSpendSelector);
  const customerInfo = useSelector(customerInfoSelector);
  const { firstName } = formatCustomerNames({
    firstName: customerInfo.first_name,
  });
  const marketingCommunicationOptIn = useSelector(
    marketingCommunicationOptInSelector,
  );
  const marketingCommunicationOptInDirty = useSelector(
    marketingCommunicationOptInDirtySelector,
  );
  const startForm = useSelector(startFormSelector);
  const locale = useSelector(localeSelector);
  const mcode = useSelector(mcodeSelector);
  const page = useSelector(pageSelector);
  const prefix = isOnBoardSpend
    ? 'onboard-spend-application'
    : 'regular-application';
  const inputs = t(`start:${prefix}.inputs`, { returnObjects: true });
  const [_, country] = locale.split('-');
  const region = startForm.region.value;
  const isUS = country === Country.US;
  const isDisallowedRegion =
    isUS && !validationFunctions.allowedRegionUS(region as RegionsUS);
  const enableQuebecInputs = region === RegionsCA.QC;
  const options = useSelector(optionsSelector) as StartOption[];
  const showNameInputs =
    options.includes(StartOption.FIRST_NAME) &&
    options.includes(StartOption.LAST_NAME);
  const brand = useSelector(brandSelector);
  const isUplift = brand == Brand.UPLIFT;

  useEffect(() => {
    dispatch(trackPageMount(Page.START));
  }, []);

  useEffect(() => {
    batch(() => {
      Object.values(QuebecOption).forEach(option => {
        dispatch(
          startFormActions.updateInputHidden({
            name: option,
            value: !enableQuebecInputs,
          }),
        );
      });
    });
  }, [enableQuebecInputs]);

  useEffect(() => {
    const [_, country] = locale.split('-');

    if (country === Country.CA && !marketingCommunicationOptInDirty)
      dispatch(setMarketingCommunicationOptIn(false));

    if (typeof inputs === 'string') {
      return;
    }
    dispatch(updateFormMeta(inputs, startFormActions));
  }, [locale, marketingCommunicationOptInDirty]);

  const learnMoreClickHandler = () => {
    dispatch(general({ page: Page.FAQ, previousPage: page }));
  };

  const onMarketingOptInChange = (value: boolean) => {
    batch(() => {
      dispatch(trackClick('marketing-communication-opt-in', { value }));
      dispatch(setMarketingCommunicationOptIn(value));
    });
  };

  const onMarketingOptInBlur = () => {
    dispatch(trackFormBlur('marketing-communication-opt-in'));
  };

  const submitForm = useCallback(() => {
    const errors = validate(startForm);

    errors.forEach((error, idx: number) => {
      if (idx === 0) {
        const errorElement = document.getElementById(error.name);
        errorElement?.focus();
      }
      dispatch(
        startFormActions.updateInputIsValid({
          name: error.name,
          msg: error.errorMsg,
        }),
      );
    });

    if (!errors.length) {
      dispatch(xhrNext());
    } else {
      dispatch(trackFocus(['startForm', errors[0].name, 'value']));
    }
  }, [startForm]);

  return (
    <div id={styles['start']}>
      {mcode != undefined && (
        <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
      )}
      {isDisallowedRegion && (
        <Alerter
          type={AlertNames.ERROR}
          i18nKey='common:mcodes.61'
          values={{ region }}
        />
      )}
      {region === RegionsUS.WI && (
        <Alerter
          type={AlertNames.NOTICE}
          i18nKey='start:notice-header-disclaimer'
        />
      )}
      <div className={styles['start-header-wrapper']}>
        <div className='header-txt'>
          {isOnBoardSpend || showNameInputs ? (
            <Trans t={t} i18nKey={`start:${prefix}.header`} />
          ) : (
            <Trans
              t={t}
              i18nKey={`start:${prefix}.header-with-name`}
              values={{ name: firstName }}
            />
          )}
        </div>
        <h3 className={styles['start-subheader']}>
          {t(`start:${prefix}.subheader.verify-details`)}{' '}
          <span className={styles['start-subheader-no-inquiry']}>
            {t(`start:${prefix}.subheader.no-inquiry`)}
          </span>{' '}
          <SimpleButton
            label={t(`start:${prefix}.subheader.learn-more`)}
            trackingLabel='learn-more'
            id='faq'
            onClickPost={learnMoreClickHandler}
            classLabel={styles['start-subheader-learn-more']}
            trackClickPayload={{ label: t('start:subheader.learn-more') }}
          />
        </h3>
      </div>
      <div className={styles['start-body']}>
        {isOnBoardSpend ? (
          <StartOnboardInfo />
        ) : (
          <StartInfo showNameInfoGroup={!showNameInputs} />
        )}
        <StartInputs />
        {!isUS && (
          <CheckboxGroup
            id={styles['start-marketing-communication-opt-in']}
            label={t(
              `start:${brand.toLocaleLowerCase()}.marketing-communication-opt-in`,
            )}
            checked={marketingCommunicationOptIn}
            onChangeHandler={onMarketingOptInChange}
            onBlurHandler={onMarketingOptInBlur}
          />
        )}
        <StartFootnotes />
        <SimpleButton
          label={t('start:submit')}
          trackingLabel={Page.START}
          classLabel={
            isDisallowedRegion
              ? `btn-primary ${styles['start-form-submit']} disabled`
              : `btn-primary ${styles['start-form-submit']}`
          }
          onClickPost={submitForm}
          trackClickPayload={{
            label: t('start:submit'),
          }}
        />
        <StartPleaseEnsure />
      </div>
    </div>
  );
};

export default StartPage;
